export const ASSESSMENT_HISTORY_KEY = "forr:assessment-history:v1";
export const ACTIVE_ASSESSMENT_KEY = "forr:active-assessment-id:v1";
export const LEGACY_PROFILE_KEY = "forr:adaptive-profile";

const safeParse = (value, fallback) => {
  try {
    const parsed = JSON.parse(value);
    return parsed == null ? fallback : parsed;
  } catch (_error) {
    return fallback;
  }
};

const numericTime = (value) => {
  const number = Number(value);
  if (Number.isFinite(number)) return number;
  const date = new Date(value).getTime();
  return Number.isFinite(date) ? date : 0;
};

const assessmentId = (record = {}) =>
  String(
    record.id ||
      `assessment-${numericTime(record.completedAt || record.t || Date.now())}`,
  );

export function normalizeAssessmentRecord(record = {}) {
  const completedAt = numericTime(record.completedAt || record.t || Date.now());
  const answers = record.answers || record.a || {};
  const identity = record.identity || record.i || null;
  const nervousSystemProfile =
    record.nervousSystemProfile ||
    record.robustProfile ||
    answers.robustProfile ||
    null;
  return {
    id: assessmentId({ ...record, completedAt }),
    schemaVersion: Number(record.schemaVersion || record.v) || 3,
    assessmentModel:
      record.assessmentModel || answers.meta?.assessmentModel || null,
    completedAt,
    updatedAt: record.updatedAt || new Date(completedAt).toISOString(),
    identity,
    answers,
    profileKey:
      record.profileKey ||
      record.engineProfileKey ||
      nervousSystemProfile?.legacyProfileKey ||
      record.profile?.key ||
      null,
    profileTitle:
      record.profileTitle ||
      nervousSystemProfile?.headline ||
      record.profile?.title ||
      null,
    dimensions: record.dimensions || null,
    baselineVector:
      record.baselineVector || nervousSystemProfile?.vector || null,
    nervousSystemProfile,
    appVersion: record.appVersion || null,
  };
}

const fromLegacy = () => {
  const legacy = safeParse(localStorage.getItem(LEGACY_PROFILE_KEY), null);
  if (!legacy?.a) return [];
  return [normalizeAssessmentRecord(legacy)];
};

export function loadAssessmentHistoryLocal() {
  try {
    const stored = safeParse(localStorage.getItem(ASSESSMENT_HISTORY_KEY), []);
    const source = Array.isArray(stored) && stored.length ? stored : fromLegacy();
    const unique = new Map();
    source.map(normalizeAssessmentRecord).forEach((record) => {
      const current = unique.get(record.id);
      if (!current || record.completedAt >= current.completedAt) unique.set(record.id, record);
    });
    return [...unique.values()].sort((a, b) => a.completedAt - b.completedAt);
  } catch (_error) {
    return [];
  }
}

export function activeAssessmentIdLocal() {
  try {
    return localStorage.getItem(ACTIVE_ASSESSMENT_KEY) || null;
  } catch (_error) {
    return null;
  }
}

const writeLegacyCompatibility = (record) => {
  if (!record) return;
  localStorage.setItem(
    LEGACY_PROFILE_KEY,
    JSON.stringify({
      v: record.schemaVersion || 3,
      id: record.id,
      a: record.answers,
      i: record.identity,
      t: record.completedAt,
      profileKey: record.profileKey,
      profileTitle: record.profileTitle,
      dimensions: record.dimensions,
      assessmentModel: record.assessmentModel,
      baselineVector: record.baselineVector,
      nervousSystemProfile: record.nervousSystemProfile,
    }),
  );
};

export function setActiveAssessmentLocal(id) {
  const history = loadAssessmentHistoryLocal();
  const record = history.find((assessment) => assessment.id === id);
  if (!record) return null;
  try {
    localStorage.setItem(ACTIVE_ASSESSMENT_KEY, record.id);
    writeLegacyCompatibility(record);
    window.dispatchEvent(
      new CustomEvent("adaptive-active-assessment-changed", {
        detail: { assessmentId: record.id, completedAt: record.completedAt },
      }),
    );
  } catch (_error) {
    // The active record remains available in memory to the current caller.
  }
  return record;
}

export function loadActiveAssessmentLocal() {
  const history = loadAssessmentHistoryLocal();
  if (!history.length) return null;
  const activeId = activeAssessmentIdLocal();
  return (
    history.find((record) => record.id === activeId) ||
    history[history.length - 1]
  );
}

const sameAssessmentSubmission = (first, second) => {
  if (!first || !second) return false;
  if (Math.abs(first.completedAt - second.completedAt) > 30_000) return false;
  try {
    return (
      JSON.stringify(first.answers) === JSON.stringify(second.answers) &&
      String(first.identity?.email || "") === String(second.identity?.email || "")
    );
  } catch (_error) {
    return false;
  }
};

export function appendAssessmentLocal(record) {
  const normalized = normalizeAssessmentRecord(record);
  const history = loadAssessmentHistoryLocal();
  const latest = history[history.length - 1];
  const nextRecord = sameAssessmentSubmission(latest, normalized)
    ? { ...normalized, id: latest.id, completedAt: latest.completedAt }
    : normalized;
  const nextHistory = [
    ...history.filter((assessment) => assessment.id !== nextRecord.id),
    nextRecord,
  ].sort((a, b) => a.completedAt - b.completedAt);

  try {
    localStorage.setItem(ASSESSMENT_HISTORY_KEY, JSON.stringify(nextHistory));
    localStorage.setItem(ACTIVE_ASSESSMENT_KEY, nextRecord.id);
    writeLegacyCompatibility(nextRecord);
  } catch (_error) {
    // The current assessment can still be displayed in this session.
  }
  return { record: nextRecord, history: nextHistory };
}

export function importAssessmentHistoryLocal(records = [], activeAssessmentId = null) {
  const current = loadAssessmentHistoryLocal();
  const merged = new Map();
  [...current, ...(Array.isArray(records) ? records : [])]
    .map(normalizeAssessmentRecord)
    .forEach((record) => {
      const existing = merged.get(record.id);
      if (!existing || record.completedAt >= existing.completedAt) {
        merged.set(record.id, record);
      }
    });
  const history = [...merged.values()].sort((a, b) => a.completedAt - b.completedAt);
  try {
    localStorage.setItem(ASSESSMENT_HISTORY_KEY, JSON.stringify(history));
  } catch (_error) {
    return history;
  }
  const selected =
    history.find((record) => record.id === activeAssessmentId) ||
    history[history.length - 1] ||
    null;
  if (selected) setActiveAssessmentLocal(selected.id);
  return history;
}

export function beginAssessmentRetakeLocal() {
  try {
    localStorage.setItem("forr:assessment-retake-in-progress:v1", "1");
  } catch (_error) {
    // Non-fatal.
  }
}

export function finishAssessmentRetakeLocal() {
  try {
    localStorage.removeItem("forr:assessment-retake-in-progress:v1");
  } catch (_error) {
    // Non-fatal.
  }
}

export function clearAllAssessmentHistoryLocal() {
  try {
    localStorage.removeItem(ASSESSMENT_HISTORY_KEY);
    localStorage.removeItem(ACTIVE_ASSESSMENT_KEY);
    localStorage.removeItem(LEGACY_PROFILE_KEY);
    localStorage.removeItem("forr:assessment-retake-in-progress:v1");
  } catch (_error) {
    // Non-fatal.
  }
}
